import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Facet } from '@coveo/headless';
import SearchLocationHero from './LocationSearchHero/LocationSearchHero';
import LocationSearchFilterBar, {
  executeFacetSearch,
} from './LocationSearchFilterBar/LocationSearchFilterBar';
import MapCoveoResultToCard from './mapCoveoResultToCard/mapCoveoResultToCard';
import {
  LocationSearchCoords,
  LocationSearchFacetControllers,
  LocationSearchFacetKey,
  SearchLocationHeroProps,
} from './LocationSearch.types';
import {
  findProfileDistanceOption,
  getLocationSearchDistanceOptions,
  mapSitesFacetsToControllers,
  resolveDefaultDistanceSelection,
} from './locationSearchFilterConfig';
import { uniqueSiteRelatedCommunities } from './uniqueSiteRelatedCommunities';
import {
  scrollToHeroCommunityPills,
  scrollToSection,
} from '../A11y/scrollToSection/scrollToSection';
import useSitesResultList from 'lib/hooks/coveo/sitesHeadless/useSitesResultList';
import useSitesQuerySummary from 'lib/hooks/coveo/sitesHeadless/useSitesQuerySummary';
import sitesHeadlessEngine from 'lib/utils/coveo/sitesHeadless/sitesHeadlessEngine';
import { getSitesHeadlessFacets } from 'lib/utils/coveo/sitesHeadless/sitesHeadlessFacets';
import { RootState } from 'src/store/store';
import {
  buildProfileAddress,
  parseProfileCoordinates,
  ProfileBasicDetails,
} from 'components/NearBySiteLocation/useNearBySiteSearchLocation';
import {
  clearCommunity,
  markSearchResetConsumed,
  setCommunities,
  setCoords,
  setDistanceFilter,
  toggleCommunity,
} from '../../features/locationSearchSlice';

const FACET_KEYS: LocationSearchFacetKey[] = ['condition', 'status', 'ageGroup', 'language'];
/** Facets the profile does not fill. The city crumb clears these. Condition is handled separately. */
const NON_PROFILE_FACET_KEYS: LocationSearchFacetKey[] = ['status', 'ageGroup', 'language'];

/** The CDP profile fields this page reads. Communities and saved sites are ignored. */
type ProfileRecord = {
  basicDetails?: ProfileBasicDetails & { distanceOptions?: string };
  conditions?: Array<{ conditionValue?: string }>;
};

/** True when Redux already holds a profile. This page does not fetch one. */
const profileIsLoaded = (data: unknown): data is ProfileRecord => {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const record = data as ProfileRecord;
  return record.basicDetails != null || Array.isArray(record.conditions);
};

/** Exact conditionValue strings. Matching against the facet is case-sensitive. */
const conditionValuesFromProfile = (data: ProfileRecord | undefined): string[] =>
  (data?.conditions ?? [])
    .map((condition) => condition?.conditionValue?.trim() ?? '')
    .filter(Boolean);

/** Same text the device-location path puts in the search box: "City, State - ZIP". */
const formatProfileSearchText = (
  details?: Pick<ProfileBasicDetails, 'city' | 'state' | 'zipCode'>
): string => {
  const locality = [details?.city?.trim(), details?.state?.trim()].filter(Boolean).join(', ');
  return [locality, details?.zipCode?.trim()].filter(Boolean).join(' - ');
};

function LocationSearch(props: SearchLocationHeroProps) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDesktopLayout = useMediaQuery(theme.breakpoints.up('lg'));
  const {
    coords,
    locationLabel,
    sortByDistance,
    selectedCommunities,
    nationwideTotal,
    communities,
    distanceLabel,
    distanceMiles,
    returnFromSiteBreadcrumb,
    searchResetVersion,
    consumedSearchResetVersion,
  } = useSelector((state: RootState) => state.locationSearch);
  const isAuthenticated = useSelector(
    (state: RootState) => state.authentication?.isAuthenticated ?? false
  );
  const profileData = useSelector((state: RootState) => state.crProfile?.profileData);
  // Set only by the site detail locality crumb. View-site alone should not beat profile location.
  const [isBreadcrumbReturn] = useState(
    () => returnFromSiteBreadcrumb && Boolean(locationLabel?.trim())
  );
  // Passed to a site click, which is what writes locationLabel. The search itself does not.
  const [siteLocality, setSiteLocality] = useState<string | undefined>(
    () => locationLabel?.trim() || undefined
  );
  // True only after a logged-in landing read the profile. Guests never set it.
  const [profileVisit, setProfileVisit] = useState(false);
  const [profileConditionValues, setProfileConditionValues] = useState<string[]>([]);
  const profileHydrateStarted = useRef(false);
  // Conditions are applied once per landing. Later filter changes do not reapply them.
  const profileConditionsApplied = useRef(false);
  // The matched profile distance. Find a Site's reset clears Redux distance after hydration,
  // so the reset effect writes this back, and the search does not treat the gap as "no distance".
  const profileDistanceRef = useRef<{ label: string; miles: number | null } | null>(null);
  // True until the city-crumb landing has cleared the filters the profile does not own.
  const pendingBreadcrumbClear = useRef(false);
  const profileSeed = useMemo(() => {
    const empty = {
      query: null as string | null,
      geocodeQuery: null as string | null,
      coords: null as LocationSearchCoords | null,
    };
    if (isBreadcrumbReturn || !isAuthenticated || !profileIsLoaded(profileData)) {
      return empty;
    }
    const query = formatProfileSearchText(profileData.basicDetails) || null;
    const parsed = parseProfileCoordinates(
      profileData.basicDetails?.latitude,
      profileData.basicDetails?.longitude
    );
    const coords =
      parsed.latitude != null && parsed.longitude != null
        ? { lat: parsed.latitude, lng: parsed.longitude }
        : null;
    // A missing coordinate is calculated from the full saved address, street included.
    const geocodeQuery = coords ? null : buildProfileAddress(profileData.basicDetails) || null;
    return { query, geocodeQuery, coords };
  }, [isAuthenticated, isBreadcrumbReturn, profileData]);
  const [facetControllers, setFacetControllers] = useState<
    LocationSearchFacetControllers | undefined
  >(undefined);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      setFacetControllers(mapSitesFacetsToControllers(getSitesHeadlessFacets()));
    } catch {
      setFacetControllers(undefined);
    }
  }, []);

  const searchOptions = useMemo(() => {
    const options: {
      latitude?: number;
      longitude?: number;
      sortByDistance: boolean;
      communities?: string[];
      distanceMiles?: number;
    } = {
      latitude: coords?.lat,
      longitude: coords?.lng,
      sortByDistance,
      communities: selectedCommunities.length > 0 ? selectedCommunities : undefined,
    };

    if (distanceMiles != null) {
      options.distanceMiles = distanceMiles;
    }

    return options;
  }, [coords, sortByDistance, selectedCommunities, distanceMiles]);

  const { state, isLoading } = useSitesResultList(sitesHeadlessEngine, searchOptions);
  const queryTotal = useSitesQuerySummary(sitesHeadlessEngine);

  useEffect(() => {
    dispatch(setCommunities(uniqueSiteRelatedCommunities(state)));
  }, [dispatch, state]);

  const distanceOptions = useMemo(
    () => getLocationSearchDistanceOptions(props.fields),
    [props.fields]
  );

  const applyDefaultDistance = useCallback(() => {
    const selection = resolveDefaultDistanceSelection(
      props.fields?.defaultDistance,
      distanceOptions
    );
    if (!selection) {
      return;
    }
    dispatch(setDistanceFilter({ label: selection.label, miles: selection.miles ?? null }));
  }, [dispatch, props.fields?.defaultDistance, distanceOptions]);

  const clearAllFilterState = useCallback(() => {
    dispatch(setDistanceFilter({ label: null, miles: null }));
    dispatch(clearCommunity());

    FACET_KEYS.forEach((key) => {
      const facet = facetControllers?.[key] as Facet | undefined;
      if (facet?.state.hasActiveValues) {
        facet.deselectAll();
      }
    });

    executeFacetSearch();
  }, [dispatch, facetControllers]);

  const handleLocationSearch = useCallback(
    (
      nextCoords: LocationSearchCoords,
      locality?: string,
      options?: { scrollToResults?: boolean; searchZipCode?: string | null }
    ) => {
      dispatch(
        setCoords({
          ...nextCoords,
          searchZipCode: options?.searchZipCode ?? null,
        })
      );
      if (locality?.trim()) {
        setSiteLocality(locality.trim());
      }
      // A distance already chosen this session (kept in redux) wins over the default.
      // A matched profile distance counts as chosen, even while Find a Site's reset has emptied redux.
      if (!distanceLabel && !profileDistanceRef.current) {
        applyDefaultDistance();
      }
      if (options?.scrollToResults !== false) {
        scrollToSection(isDesktopLayout);
      }
    },
    [dispatch, applyDefaultDistance, distanceLabel, isDesktopLayout]
  );

  const handleCommunitySelect = useCallback(
    (nextCommunity: string) => {
      dispatch(toggleCommunity(nextCommunity));
      if (!isDesktopLayout) {
        scrollToHeroCommunityPills();
      }
    },
    [dispatch, isDesktopLayout]
  );

  const handleDistanceChange = useCallback(
    (nextDistanceLabel: string | null, nextDistanceMiles: number | undefined) => {
      dispatch(setDistanceFilter({ label: nextDistanceLabel, miles: nextDistanceMiles ?? null }));
    },
    [dispatch]
  );

  // One-shot clearing after resetSearch. Consumed via redux (not a ref) so a
  // remount of this page does not re-run the same reset and wipe the facets
  // that survived in the Coveo engine singleton.
  useEffect(() => {
    if (searchResetVersion === 0 || searchResetVersion === consumedSearchResetVersion) {
      return;
    }
    // "Find a Site" dispatches resetSearch before client-side navigation, so
    // this effect fires on the first render — while facetControllers is still
    // undefined (it is mapped asynchronously in the mount effect above).
    // Consuming the reset now would make facet.deselectAll() a silent no-op and
    // the stale selections in the Coveo singleton would survive. Wait for the
    // controllers; this effect re-runs as soon as they are mapped.
    if (!facetControllers) {
      return;
    }
    dispatch(markSearchResetConsumed());
    clearAllFilterState();
    // The clear above also wipes the profile distance set on landing; put it back.
    const profileDistance = profileDistanceRef.current;
    if (profileDistance) {
      dispatch(setDistanceFilter(profileDistance));
    }
  }, [
    consumedSearchResetVersion,
    dispatch,
    searchResetVersion,
    clearAllFilterState,
    facetControllers,
  ]);

  // Profile landing. Runs once when the user is logged in. Reads the profile already in Redux.
  useEffect(() => {
    if (!isAuthenticated || profileHydrateStarted.current) {
      return;
    }
    profileHydrateStarted.current = true;
    if (!profileIsLoaded(profileData)) {
      return;
    }

    const distance = findProfileDistanceOption(
      profileData.basicDetails?.distanceOptions,
      props.fields
    );
    pendingBreadcrumbClear.current = isBreadcrumbReturn;
    profileDistanceRef.current = distance
      ? { label: distance.label, miles: distance.miles ?? null }
      : null;
    if (profileDistanceRef.current) {
      dispatch(setDistanceFilter(profileDistanceRef.current));
    }
    setProfileConditionValues(conditionValuesFromProfile(profileData));
    setProfileVisit(true);
    // Hydrate once. Profile data is read from that render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isAuthenticated]);

  // City crumb only: keep the searched city, clear the filters the profile does not fill.
  // Find a Site already cleared these through resetSearch.
  useEffect(() => {
    if (!profileVisit || !pendingBreadcrumbClear.current || !facetControllers) {
      return;
    }
    pendingBreadcrumbClear.current = false;
    dispatch(clearCommunity());

    let changed = false;
    NON_PROFILE_FACET_KEYS.forEach((key) => {
      const facet = facetControllers[key] as Facet | undefined;
      if (facet?.state.hasActiveValues) {
        facet.deselectAll();
        changed = true;
      }
    });
    if (changed) {
      executeFacetSearch();
    }
  }, [dispatch, facetControllers, profileVisit]);

  // Profile conditions, once per landing. Coveo only has condition values after a search
  // has returned, so this listens to the facet until it has values, applies, then ignores later updates.
  useEffect(() => {
    if (!profileVisit || !coords || profileConditionsApplied.current) {
      return;
    }
    const facet = facetControllers?.condition;
    if (!facet) {
      return;
    }

    const applyOnce = () => {
      if (profileConditionsApplied.current) {
        return;
      }
      const values = facet.state.values ?? [];
      if (profileConditionValues.length > 0 && values.length === 0) {
        return;
      }
      profileConditionsApplied.current = true;

      // Replace any condition left from the last visit with the profile set.
      const wanted = new Set(profileConditionValues);
      let changed = false;
      if (facet.state.hasActiveValues) {
        facet.deselectAll();
        changed = true;
      }
      // Exact, case-sensitive match. A profile condition that Coveo did not return is skipped.
      values.forEach((facetValue) => {
        if (wanted.has(facetValue.value)) {
          facet.toggleSelect(facetValue);
          changed = true;
        }
      });
      if (changed) {
        executeFacetSearch();
      }
    };

    applyOnce();
    return facet.subscribe(applyOnce);
  }, [coords, facetControllers, profileConditionValues, profileVisit]);

  const handleClearAllFilters = useCallback(() => {
    // After Clear all, the next search uses the CMS default distance again.
    profileDistanceRef.current = null;
    clearAllFilterState();
  }, [clearAllFilterState]);

  return (
    <>
      <SearchLocationHero
        {...props}
        profileLocationQuery={profileSeed.query}
        profileGeocodeQuery={profileSeed.geocodeQuery}
        profileCoords={profileSeed.coords}
        nationwideTotal={nationwideTotal}
        communities={communities}
        selectedCommunities={selectedCommunities}
        onCommunitySelect={handleCommunitySelect}
        onLocationSearch={handleLocationSearch}
      />
      <LocationSearchFilterBar
        {...props}
        sitesFoundCount={queryTotal}
        hasLocationContext={Boolean(coords)}
        selectedDistanceLabel={distanceLabel}
        onDistanceChange={handleDistanceChange}
        facetControllers={facetControllers}
        hasCommunityFilter={selectedCommunities.length > 0}
        onClearAllFilters={handleClearAllFilters}
      />
      <MapCoveoResultToCard
        results={state}
        isLoading={isLoading}
        fields={props.fields}
        coords={coords}
        distanceMiles={distanceMiles ?? undefined}
        hasLocationContext={Boolean(coords)}
        searchLocality={siteLocality}
        onClearAllFilters={handleClearAllFilters}
      />
    </>
  );
}

export default LocationSearch;
